import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { TEMPLATE_CATEGORIES, ALL_TEMPLATE_IDS, TEMPLATE_REGISTRY } from '../../templates/templateDesignSystem';
import { TEMPLATE_METADATA } from '../../templates/templateMetadata';
import useGalleryTheme from './hooks/useGalleryTheme';
import TemplateCard from './components/TemplateCard';
import CoverLetterCard from './components/CoverLetterCard';
import CustomizePanel from './components/CustomizePanel';
import CategoryTabs from './components/CategoryTabs';
import TemplateFilterDrawer from './components/TemplateFilterDrawer';
import { COVER_TEMPLATES } from './registry/coverLetters';
import { Search, SlidersHorizontal } from 'lucide-react';
import './styles/gallery.css';

import { useLocation } from 'react-router-dom';

export default function TemplateGallery({
  selectedTemplate,
  setTemplate,
  setTemplateVersion,
  accentColor,
  setAccentColor,
  fontScale = 1,
  setFontScale,
  fontFamily,
  setFontFamily,
  resumeData,
  domainLabel,
  onChangeDomain,
  theme = 'dark',
  coverTemplate,
  setCoverTemplate,
  activeCoverLetter,
}) {
  const location = useLocation();
  const isDark = theme === 'dark';
  const T = useGalleryTheme(isDark);
  const isCoverLetterPage = location.pathname.includes('/cover-letter');
  const galleryTab = isCoverLetterPage ? 'cover' : 'resume';

  // State Declarations
  const [activeCategory, setActiveCategory] = useState('all');
  const [showCustomize, setShowCustomize] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeDetailId, setActiveDetailId] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem('mmr_favorites');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [filters, setFilters] = useState({
    industry: 'all',
    color: 'all',
    complexity: 'all',
    requireSidebar: false,
    requirePhoto: false,
    premiumOnly: false,
    sortBy: 'popular'
  });

  // Dynamic Rule-based AI Recommendation scoring
  const recommendedId = useMemo(() => {
    if (!resumeData) return 'harvard-classic';
    const workItems = resumeData.work || resumeData.experience || [];
    const expCount = workItems.length;
    let targetExp = 'Mid';
    if (expCount >= 5) targetExp = 'Senior';
    if (expCount >= 8) targetExp = 'Leadership';
    if (expCount === 0) targetExp = 'Student';

    let bestTmpl = 'harvard-classic';
    let bestScore = -1;

    Object.keys(TEMPLATE_METADATA).forEach(key => {
      const meta = TEMPLATE_METADATA[key];
      let score = 0;
      if (meta.recommendation?.experience?.includes(targetExp)) score += 50;

      const summaryText = `${resumeData.summary || ''} ${workItems[0]?.position || ''}`.toLowerCase();
      meta.recommendation?.roles?.forEach(role => {
        if (summaryText.includes(role.toLowerCase())) score += 35;
      });

      if (score > bestScore) {
        bestScore = score;
        bestTmpl = key;
      }
    });
    return bestTmpl.replace(/-/g, '_');
  }, [resumeData]);

  // Sync favorites to local storage
  useEffect(() => {
    localStorage.setItem('mmr_favorites', JSON.stringify(favorites));
  }, [favorites]);

  // Normalize ID to canonical kebab-case before propagating upward.
  // Gallery card IDs are in underscore format (from ALL_TEMPLATE_IDS → templateDesignSystem).
  // setTemplate receives kebab — this is the single normalization point for the entire pipeline.
  const handleSelect = useCallback(id => {
    const canonical = id.replace(/_/g, '-').toLowerCase();
    if (process.env.NODE_ENV === 'development') {
      console.log(`[PIPE:template S1:Gallery] raw=${id} → canonical=${canonical}`);
    }
    setTemplate(canonical);
    // Update version from template metadata if available
    if (setTemplateVersion) {
      const meta = TEMPLATE_METADATA?.[canonical] || TEMPLATE_METADATA?.[id];
      const version = meta?.version || "2.2.0";
      setTemplateVersion(version);
    }
  }, [setTemplate, setTemplateVersion]);

  const toggleFavorite = useCallback((id) => {
    const registryId = id.replace(/_/g, '-');
    setFavorites(prev =>
      prev.includes(registryId) ? prev.filter(x => x !== registryId) : [...prev, registryId]
    );
  }, []);

  const handleResetFilters = () => {
    setFilters({
      industry: 'all',
      color: 'all',
      complexity: 'all',
      requireSidebar: false,
      requirePhoto: false,
      premiumOnly: false,
      sortBy: 'popular'
    });
    setSearchQuery('');
  };

  // Complex Client-side Filtering & Sorting pipeline
  const filteredIds = useMemo(() => {
    let ids = ALL_TEMPLATE_IDS;

    // Filter by category tab
    if (activeCategory === 'favorites') {
      ids = favorites.map(id => id.replace(/-/g, '_'));
    } else if (activeCategory !== 'all') {
      const cat = TEMPLATE_CATEGORIES.find(c => c.id === activeCategory);
      ids = cat ? cat.templates : ALL_TEMPLATE_IDS;
    }



    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      ids = ids.filter(id => {
        const meta = TEMPLATE_REGISTRY[id];
        if (!meta) return false;
        return (
          meta.name?.toLowerCase().includes(query) ||
          meta.tags?.some(tag => tag.toLowerCase().includes(query)) ||
          meta.recommendation?.roles?.some(role => role.toLowerCase().includes(query))
        );
      });
    }

    // Filter by dropdown choices
    ids = ids.filter(id => {
      const meta = TEMPLATE_REGISTRY[id];
      if (!meta) return false;

      if (filters.industry !== 'all' && !meta.recommendation?.roles?.includes(filters.industry)) return false;
      if (filters.complexity !== 'all' && meta.complexity !== filters.complexity) return false;
      if (filters.requireSidebar && !meta.features?.sidebar) return false;
      if (filters.requirePhoto && !meta.features?.photo) return false;
      if (filters.premiumOnly && !meta.premium) return false;

      if (filters.color !== 'all') {
        const hasColor = meta.themes?.some(themeOpt => themeOpt.id.toLowerCase() === filters.color.toLowerCase());
        if (!hasColor) return false;
      }
      return true;
    });

    // Sorting
    return [...ids].sort((a, b) => {
      const metaA = TEMPLATE_REGISTRY[a];
      const metaB = TEMPLATE_REGISTRY[b];
      if (!metaA || !metaB) return 0;

      if (filters.sortBy === 'newest') {
        return new Date(metaB.created_at || 0) - new Date(metaA.created_at || 0);
      }
      if (filters.sortBy === 'ats') {
        return (metaB.ratings?.ats || 0) - (metaA.ratings?.ats || 0);
      }
      return (metaB.popularity || 0) - (metaA.popularity || 0); // default Sort by Popularity
    });
  }, [activeCategory, searchQuery, filters, favorites]);

  const activeDetailMeta = useMemo(() => {
    if (!activeDetailId) return null;
    return TEMPLATE_REGISTRY[activeDetailId.replace(/-/g, '_')];
  }, [activeDetailId]);

  return (
    <div className="tg-root tg-scrollbar" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: T.pageBg, overflow: 'hidden', position: 'relative' }}>
      


      {/* Header Panel */}
      <div style={{ flexShrink: 0, background: T.headerBg, borderBottom: `1px solid ${T.borderMain}`, padding: isMobile ? '76px 16px 10px' : '84px 20px 0', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' }}>
        
        {/* Title row */}
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          justifyContent: 'space-between', 
          gap: isMobile ? 10 : 12,
          marginBottom: 12 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: isMobile ? '100%' : 'auto' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: isDark ? 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(139,92,246,0.3))' : 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))', border: `1px solid ${isDark ? 'rgba(167,139,250,0.35)' : 'rgba(139,92,246,0.25)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {galleryTab === 'resume' ? (
                <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={isDark ? 'rgba(167,139,250,1)' : '#7c3aed'} strokeWidth={1.7}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                </svg>
              ) : (
                <span style={{ fontSize: '15px' }}>✉️</span>
              )}
            </div>
            <div>
              <h2 style={{ fontSize: 14, fontWeight: 800, color: T.textPrimary, margin: 0 }}>
                {galleryTab === 'resume' ? 'Template Gallery' : 'Cover Letter Templates'}
              </h2>
              <p style={{ fontSize: 9, color: T.textMuted, margin: '2px 0 0' }}>
                {galleryTab === 'resume' ? `${filteredIds.length} styles matching` : `${COVER_TEMPLATES.length} cover letter styles`}
              </p>
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 8, 
            width: isMobile ? '100%' : 'auto', 
            justifyContent: isMobile ? 'space-between' : 'flex-end',
            flexWrap: 'wrap'
          }}>
            <button onClick={() => setShowCustomize(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 9, cursor: 'pointer', background: showCustomize ? 'rgba(139,92,246,0.1)' : T.pillBg, border: `1px solid ${T.borderCard}`, flexShrink: 0 }}>
              <span style={{ fontSize: '9.5px', fontWeight: 700, color: T.textSecondary }}>Customize</span>
            </button>
          </div>
        </div>

        {/* Search Bar / Filter Trigger */}
        {galleryTab === 'resume' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, color: T.textMuted }} />
              <input
                type="text"
                placeholder="Search templates by role, tag..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '100%', padding: '6px 10px 6px 30px', fontSize: '11px', borderRadius: 8, background: T.pillBg, border: `1px solid ${T.borderCard}`, color: T.textPrimary, outline: 'none' }}
              />
            </div>
            <button onClick={() => setShowFilters(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, background: showFilters ? 'rgba(99,102,241,0.12)' : T.pillBg, border: `1px solid ${T.borderCard}`, cursor: 'pointer', color: T.textSecondary }}>
              <SlidersHorizontal size={13} />
              <span style={{ fontSize: '10px', fontWeight: 600 }}>Filters</span>
            </button>
          </div>
        )}

        {/* Collapsible Filters Drawer */}
        {galleryTab === 'resume' && (
          <TemplateFilterDrawer isOpen={showFilters} filters={filters} setFilters={setFilters} onReset={handleResetFilters} T={T} />
        )}

        {/* Dynamic Category Tabs */}
        {galleryTab === 'resume' && (
          <CategoryTabs activeCategory={activeCategory} setActiveCategory={setActiveCategory} favoritesCount={favorites.length} T={T} />
        )}
      </div>

      {/* Customize Panel Drawer */}
      {showCustomize && (
        <CustomizePanel accentColor={accentColor} setAccentColor={setAccentColor} fontScale={fontScale} setFontScale={setFontScale} fontFamily={fontFamily} setFontFamily={setFontFamily} T={T} />
      )}

      {/* Grid list container */}
      <div className="tg-scrollbar pb-20 md:pb-6" style={{ flex: 1, overflowY: 'auto', padding: '14px 20px 0' }}>
        


        {galleryTab === 'resume' ? (
          <div>
            {/* Template Gallery Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredIds.map(id => (
                <TemplateCard
                  key={id}
                  templateId={id}
                  isActive={selectedTemplate === id}
                  isRecommended={id === recommendedId}
                  onClick={() => handleSelect(id)}
                  globalAccent={accentColor}
                  T={T}
                  resumeData={resumeData}
                  isFavorite={favorites.includes(id.replace(/_/g, '-'))}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
            
            {filteredIds.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: T.textMuted, fontSize: '11px' }}>
                No templates match your selected filters. <span onClick={handleResetFilters} style={{ color: '#6366f1', cursor: 'pointer', fontWeight: 600 }}>Reset Filters</span>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {COVER_TEMPLATES.map(tmpl => (
              <CoverLetterCard key={tmpl.id} template={tmpl} isActive={coverTemplate === tmpl.id} onClick={() => setCoverTemplate(tmpl.id)} globalAccent={accentColor} T={T} />
            ))}
          </div>
        )}
      </div>

      {/* Footer bar */}
      {galleryTab === 'resume' && (
        <div style={{ flexShrink: 0, padding: '8px 20px 10px', borderTop: `1px solid ${T.borderMain}`, background: T.footerBg, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          {[
            { color: '#059669', label: 'ATS Perfect 99–100' },
            { color: '#0369a1', label: 'ATS Safe 95–98' },
            { color: '#b45309', label: 'Caution < 95' },
          ].map(({ color, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: color, boxShadow: `0 0 5px ${color}80` }} />
              <span style={{ fontSize: '8px', color: T.textMuted, fontWeight: 500 }}>{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
